import { InjectionToken, Service, signal, type Type } from '@angular/core';

export const DIALOG_DATA = new InjectionToken<unknown>('DIALOG_DATA');

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

export class DialogRef<TResult = unknown> {
  constructor(private readonly settle: (result: TResult | undefined) => void) {}

  close(result?: TResult): void {
    this.settle(result);
  }
}

export interface OpenDialog {
  readonly id: number;
  readonly component: Type<unknown>;
  readonly data: unknown;
  readonly ref: DialogRef<unknown>;
  readonly size: DialogSize;
  readonly label: string;
}

export interface DialogOptions<TData> {
  readonly data?: TData;
  readonly size?: DialogSize;
  readonly label?: string;
}

@Service()
export class DialogService {
  private nextId = 0;

  private readonly stack = signal<readonly OpenDialog[]>([]);

  readonly dialogs = this.stack.asReadonly();

  open<TResult, TData = unknown>(
    component: Type<unknown>,
    options: DialogOptions<TData> = {},
  ): Promise<TResult | undefined> {
    return new Promise<TResult | undefined>((resolve) => {
      const id = this.nextId++;

      const ref = new DialogRef<TResult>((result) => {
        this.stack.update((dialogs) => dialogs.filter((dialog) => dialog.id !== id));
        resolve(result);
      });

      this.stack.update((dialogs) => [
        ...dialogs,
        {
          id,
          component,
          data: options.data ?? null,
          ref: ref as DialogRef<unknown>,
          size: options.size ?? 'md',
          label: options.label ?? 'Dialog',
        },
      ]);
    });
  }
}
